import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, ProgressBar, Spinner, Alert, Form, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import "../components/SchoolAdmin.css";
import {
    FaFire,
    FaThermometerHalf,
    FaSnowflake,
    FaChartLine,
    FaSchool,
    FaUserGraduate,
    FaExclamationTriangle,
    FaSync,
    FaInfoCircle
} from "react-icons/fa";

import {
    whoami,
    getTrialConversionMetrics,
    getColdTrials,
    getCoolTrials,
    getWarmTrials,
    getHotTrials,
    getHotTrialSchools,
    getWarmTrialSchools,
    getCoolTrialSchools,
    getColdTrialSchools,
    getDeptEdStudentMetrics,
    getTrialHealthData,
    getAssessmentScreener,
    getCountries
} from "../services/APIClient";

function TrialTrackerDash() {
    const [teacherid, setTeacherid] = useState(null);
    const [name, setName] = useState("");
    const [userCountryId, setUserCountryId] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [error, setError] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [isPortrait, setIsPortrait] = useState(
        window.matchMedia("(orientation: portrait)").matches
    );

    // Configuration options
    const [countries, setCountries] = useState([]);
    const [grade1Screeners, setGrade1Screeners] = useState([]);
    const [grade2Screeners, setGrade2Screeners] = useState([]);

    // Configuration parameters with updated defaults
    const [config, setConfig] = useState({
        countryId: "",
        academicYear: "25-26",
        trialStartDate: "2025-08-01",
        screeningStartDate: "2025-08-01",
        grade1AssessmentId: "",
        grade2AssessmentId: "",
        doeEnrollmentYear: "2024"
    });

    // State for dashboard data
    const [conversionData, setConversionData] = useState(null);
    const [deptEdStudents, setDeptEdStudents] = useState({
        junior: { totalStudents: 0, screened: 0, percentageScreened: 0 },
        senior: { totalStudents: 0, screened: 0, percentageScreened: 0 },
        overall: { totalStudents: 0, screened: 0, percentageScreened: 0 }
    });
    const [trialHealthData, setTrialHealthData] = useState([
        { range: "75-100%", count: 0, color: "#10b981" },
        { range: "50-74%", count: 0, color: "#3b82f6" },
        { range: "25-49%", count: 0, color: "#f59e0b" },
        { range: "0-24%", count: 0, color: "#ef4444" }
    ]);

    const [hotTrialsData, setHotTrialsData] = useState(null);
    const [warmTrialsData, setWarmTrialsData] = useState(null);
    const [coolTrialsData, setCoolTrialsData] = useState(null);
    const [coldTrialsData, setColdTrialsData] = useState(null);

    // State for school lists
    const [hotTrialSchools, setHotTrialSchools] = useState([]);
    const [warmTrialSchools, setWarmTrialSchools] = useState([]);
    const [coolTrialSchools, setCoolTrialSchools] = useState([]);
    const [coldTrialSchools, setColdTrialSchools] = useState([]);

    // State for expand/collapse and loading
    const [expandedSections, setExpandedSections] = useState({
        hot: false,
        warm: false,
        cool: false,
        cold: false
    });

    const [loadingSchools, setLoadingSchools] = useState({
        hot: false,
        warm: false,
        cool: false,
        cold: false
    });

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (config.countryId) {
            loadScreeners();
        }
    }, [config.countryId]);

    const loadInitialData = async () => {
        setLoadingConfig(true);
        try {
            const userResponse = await whoami();
            console.log("User info response:", userResponse);
            setTeacherid(userResponse.data.stakeholder.id);
            setName(userResponse.data.stakeholder.name);

            const countryIdFromUser = userResponse.data.stakeholder.countryId ||
                userResponse.data.stakeholder.country_id ||
                userResponse.data.stakeholder.Country ||
                userResponse.data.stakeholder.countryID;

            console.log("User's country ID:", countryIdFromUser);
            setUserCountryId(countryIdFromUser);

            const countriesResponse = await getCountries();
            console.log("Countries loaded:", countriesResponse);
            setCountries(countriesResponse);

            const irelandCountry = countriesResponse.find(
                c => c.name?.toLowerCase() === 'ireland' ||
                    c.name?.toLowerCase() === 'republic of ireland' ||
                    c.code?.toLowerCase() === 'ie'
            );

            console.log("Ireland country found:", irelandCountry);

            if (!irelandCountry) {
                console.error("Republic of Ireland not found in countries list!");
            }

            const irelandId = irelandCountry?.id || "";
            console.log("Setting Ireland ID:", irelandId);

            setConfig(prev => ({
                ...prev,
                countryId: irelandId
            }));

            setLoadingConfig(false);
        } catch (err) {
            console.error("Error loading initial data:", err);
            console.log("Error details:", err.response?.data || err.message);
            setError("Failed to load configuration options.");
            setLoadingConfig(false);
        }
    };

    const loadScreeners = async () => {
        console.log("Loading screeners for country ID:", config.countryId);

        try {
            console.log("Attempting to fetch Grade 1 screeners");

            let grade1Params = {
                countryid: config.countryId,
                grade_level: 1,
                active: true
            };

            let grade1Response = await getAssessmentScreener(grade1Params);
            console.log("Grade 1 response:", grade1Response);

            if (!grade1Response || !Array.isArray(grade1Response) || grade1Response.length === 0) {
                setError("No Grade 1 screeners found for selected country");
                return;
            }

            setGrade1Screeners(grade1Response);

            if (grade1Response && grade1Response.length > 0 && !config.grade1AssessmentId) {
                console.log("Auto-selecting Grade 1 screener:", grade1Response[0]);
                setConfig(prev => ({
                    ...prev,
                    grade1AssessmentId: grade1Response[0].id
                }));
            }

            console.log("Attempting to fetch Grade 2 screeners");

            let grade2Params = {
                countryid: config.countryId,
                grade_level: 2,
                active: true
            };

            let grade2Response = await getAssessmentScreener(grade2Params);
            console.log("Grade 2 response:", grade2Response);

            if (!grade2Response || !Array.isArray(grade2Response) || grade2Response.length === 0) {
                setError("No Grade 2 screeners found for selected country");
                return;
            }

            setGrade2Screeners(grade2Response);

            if (grade2Response && grade2Response.length > 0 && !config.grade2AssessmentId) {
                console.log("Auto-selecting Grade 2 screener:", grade2Response[0]);
                setConfig(prev => ({
                    ...prev,
                    grade2AssessmentId: grade2Response[0].id
                }));
            }
        } catch (err) {
            console.error("Error loading screeners:", err);
            console.log("Error details:", err.response?.data || err.message);
            setError("Failed to load assessment screeners. Please try again.");
        }
    };

    const loadDashboardData = async () => {
        if (!config.countryId) {
            setError("Country selection is required.");
            return;
        }

        if (!config.grade1AssessmentId || !config.grade2AssessmentId) {
            setError("Please select both Grade 1 and Grade 2 Assessment Screeners.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log("Loading dashboard data with params:", config);

            const [
                conversionRes,
                deptEdRes,
                hotRes,
                warmRes,
                coolRes,
                coldRes
            ] = await Promise.all([
                getTrialConversionMetrics(
                    config.countryId,
                    config.trialStartDate,
                    config.screeningStartDate,
                    config.grade1AssessmentId,
                    config.doeEnrollmentYear,
                    config.academicYear
                ),
                getDeptEdStudentMetrics(
                    config.countryId,
                    config.academicYear,
                    config.trialStartDate,
                    config.screeningStartDate,
                    config.grade1AssessmentId,
                    config.grade2AssessmentId,
                    config.doeEnrollmentYear
                ),
                getHotTrials(
                    config.countryId,
                    config.academicYear,
                    config.trialStartDate,
                    config.screeningStartDate,
                    config.grade1AssessmentId,
                    config.grade2AssessmentId,
                    config.doeEnrollmentYear
                ),
                getWarmTrials(
                    config.countryId,
                    config.academicYear,
                    config.trialStartDate,
                    config.screeningStartDate,
                    config.grade1AssessmentId,
                    config.grade2AssessmentId,
                    config.doeEnrollmentYear
                ),
                getCoolTrials(
                    config.countryId,
                    config.academicYear,
                    config.trialStartDate,
                    config.screeningStartDate,
                    config.grade1AssessmentId,
                    config.grade2AssessmentId,
                    config.doeEnrollmentYear
                ),
                getColdTrials(
                    config.countryId,
                    config.academicYear,
                    config.trialStartDate,
                    config.screeningStartDate,
                    config.grade1AssessmentId,
                    config.grade2AssessmentId,
                    config.doeEnrollmentYear
                )
            ]);

            const deptEdData = deptEdRes.data || deptEdRes;

            setConversionData(conversionRes.data);
            setDeptEdStudents(deptEdData);
            setTrialHealthData([
                { range: "75-100%", count: hotRes.data.trial_schools_total, color: "#10b981" },
                { range: "50-74%", count: warmRes.data.trial_schools_total, color: "#3b82f6" },
                { range: "25-49%", count: coolRes.data.trial_schools_total, color: "#f59e0b" },
                { range: "0-24%", count: coldRes.data.trial_schools_total, color: "#ef4444" }
            ]);
            setHotTrialsData(hotRes.data);
            setWarmTrialsData(warmRes.data);
            setCoolTrialsData(coolRes.data);
            setColdTrialsData(coldRes.data);

            setHotTrialSchools([]);
            setWarmTrialSchools([]);
            setCoolTrialSchools([]);
            setColdTrialSchools([]);

            setExpandedSections({
                hot: false,
                warm: false,
                cool: false,
                cold: false
            });

            setDataLoaded(true);
            setLoading(false);
        } catch (err) {
            console.error("Error loading dashboard data:", err);
            console.log("Error details:", err.response?.data || err.message);
            setError("Failed to load dashboard data. Please check your parameters and try again.");
            setLoading(false);
        }
    };

    const loadSchoolList = async (category) => {
        const existingData = {
            hot: hotTrialSchools,
            warm: warmTrialSchools,
            cool: coolTrialSchools,
            cold: coldTrialSchools
        };

        if (existingData[category].length > 0) {
            return;
        }

        setLoadingSchools(prev => ({ ...prev, [category]: true }));

        try {
            const apiMap = {
                hot: getHotTrialSchools,
                warm: getWarmTrialSchools,
                cool: getCoolTrialSchools,
                cold: getColdTrialSchools
            };

            const response = await apiMap[category](
                config.countryId,
                config.academicYear,
                config.trialStartDate,
                config.screeningStartDate,
                config.grade1AssessmentId,
                config.grade2AssessmentId,
                config.doeEnrollmentYear
            );

            const setterMap = {
                hot: setHotTrialSchools,
                warm: setWarmTrialSchools,
                cool: setCoolTrialSchools,
                cold: setColdTrialSchools
            };

            setterMap[category](response.data);
            setLoadingSchools(prev => ({ ...prev, [category]: false }));
        } catch (err) {
            console.error(`Error loading ${category} school list:`, err);
            setLoadingSchools(prev => ({ ...prev, [category]: false }));
            setError(`Failed to load ${category} school list.`);
        }
    };

    const handleConfigChange = (field, value) => {
        console.log(`Config change: ${field} = ${value}`);
        setConfig(prev => ({ ...prev, [field]: value }));

        if (field === 'countryId') {
            setGrade1Screeners([]);
            setGrade2Screeners([]);
            setConfig(prev => ({
                ...prev,
                countryId: value,
                grade1AssessmentId: "",
                grade2AssessmentId: ""
            }));
        }
    };

    const getCountryName = (countryId) => {
        const country = countries.find(c => c.id === countryId);
        return country?.name || 'Unknown';
    };

    const SectionHeader = ({ title, tooltip }) => (
        <div className="d-flex align-items-center mb-3">
            <h5 className="text-uppercase fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {title}
            </h5>
            {tooltip && (
                <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip>{tooltip}</Tooltip>}
                >
                    <span className="ms-2" style={{ cursor: 'help' }}>
                        <FaInfoCircle size={14} color="#6b7280" />
                    </span>
                </OverlayTrigger>
            )}
        </div>
    );

    const MetricCard = ({ title, value, subtitle, icon: Icon, color, tooltip }) => (
        <Card className="class-admin-card h-100">
            <Card.Body className="class-admin-card-body">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                        <p className="text-muted text-uppercase mb-1 d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                            {title}
                            {tooltip && (
                                <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 500, hide: 400 }}
                                    trigger={['hover', 'focus']}
                                    overlay={
                                        <Tooltip id={`tooltip-${title}`}>
                                            {tooltip}
                                        </Tooltip>
                                    }
                                    popperConfig={{
                                        strategy: 'fixed',
                                        modifiers: [
                                            {
                                                name: 'flip',
                                                enabled: true,
                                                options: {
                                                    fallbackPlacements: ['bottom', 'right', 'left'],
                                                },
                                            },
                                            {
                                                name: 'preventOverflow',
                                                enabled: true,
                                                options: {
                                                    boundary: 'viewport',
                                                    padding: 8,
                                                },
                                            },
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, 8],
                                                },
                                            },
                                        ],
                                    }}
                                >
                                    <span className="ms-1" style={{ cursor: 'help', display: 'inline-flex' }}>
                                        <FaInfoCircle size={12} />
                                    </span>
                                </OverlayTrigger>
                            )}
                        </p>
                        <h2 className="mb-1 fw-bold" style={{ color: color || "#1f2937", fontSize: '2rem' }}>
                            {value}
                        </h2>
                        {subtitle && (
                            <p className="text-muted mb-0" style={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                                {subtitle}
                            </p>
                        )}
                    </div>
                    {Icon && (
                        <div
                            className="class-admin-icon"
                            style={{ backgroundColor: `${color}15`, color: color }}
                        >
                            <Icon size={24} />
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );

    const HealthCard = ({ data }) => {
        const hasSchools = data.count > 0;
        
        return (
            <Card className="class-admin-card h-100 text-center">
                <Card.Body>
                    <div
                        className="rounded p-3 mb-3 fw-bold"
                        style={{
                            backgroundColor: `${data.color}15`,
                            color: data.color,
                            fontSize: '0.875rem'
                        }}
                    >
                        {data.range}
                    </div>
                    <h2 className="mb-1 fw-bold" style={{ fontSize: '2rem', color: hasSchools ? '#1f2937' : '#9ca3af' }}>
                        {data.count}
                    </h2>
                    <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        {data.count === 1 ? 'school' : 'schools'}
                    </p>
                    {!hasSchools && (
                        <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.75rem' }}>
                            No active trials in this range
                        </p>
                    )}
                </Card.Body>
            </Card>
        );
    };

    const DeptEdMetricsCard = () => {
        if (!deptEdStudents) return null;

        const hasData = deptEdStudents.overall.totalStudents > 0;

        return (
            <Card className="class-admin-card mt-4">
                <Card.Header className="bg-white border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div
                                className="class-admin-icon me-2"
                                style={{ backgroundColor: '#6366f115', color: '#6366f1', padding: '8px' }}
                            >
                                <FaUserGraduate size={18} />
                            </div>
                            <h6 className="mb-0 fw-bold">Department of Education Students - Screening Coverage</h6>
                        </div>
                        <OverlayTrigger
                            placement="left"
                            overlay={
                                <Tooltip>
                                    Shows how many students from DOE enrollment data have completed screenings. 
                                    This measures your market penetration against official enrollment numbers.
                                </Tooltip>
                            }
                        >
                            <span style={{ cursor: 'help' }}>
                                <FaInfoCircle size={14} color="#6b7280" />
                            </span>
                        </OverlayTrigger>
                    </div>
                </Card.Header>
                <Card.Body>
                    {!hasData ? (
                        <div className="text-center text-muted py-4">
                            <p>No DOE enrollment data available for the selected filters.</p>
                            <p className="small mb-0">This may occur if there are no active trials matching your criteria.</p>
                        </div>
                    ) : (
                        <Row>
                            <Col md={4} className="mb-3 mb-md-0">
                                <div className="p-3 bg-light rounded">
                                    <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem' }}>
                                        Junior Infants
                                    </h6>
                                    <div className="d-flex justify-content-between mb-2" style={{ fontSize: '0.875rem' }}>
                                        <span className="text-muted">Total Students:</span>
                                        <strong>{deptEdStudents.junior.totalStudents.toLocaleString()}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3" style={{ fontSize: '0.875rem' }}>
                                        <span className="text-muted">Screened:</span>
                                        <strong>{deptEdStudents.junior.screened.toLocaleString()}</strong>
                                    </div>
                                    <ProgressBar
                                        now={deptEdStudents.junior.percentageScreened}
                                        variant="success"
                                        style={{ height: '8px' }}
                                        className="mb-2"
                                    />
                                    <div className="text-center">
                                        <Badge bg="success">{deptEdStudents.junior.percentageScreened}% Screened</Badge>
                                    </div>
                                </div>
                            </Col>

                            <Col md={4} className="mb-3 mb-md-0">
                                <div className="p-3 bg-light rounded">
                                    <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem' }}>
                                        Senior Infants
                                    </h6>
                                    <div className="d-flex justify-content-between mb-2" style={{ fontSize: '0.875rem' }}>
                                        <span className="text-muted">Total Students:</span>
                                        <strong>{deptEdStudents.senior.totalStudents.toLocaleString()}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3" style={{ fontSize: '0.875rem' }}>
                                        <span className="text-muted">Screened:</span>
                                        <strong>{deptEdStudents.senior.screened.toLocaleString()}</strong>
                                    </div>
                                    <ProgressBar
                                        now={deptEdStudents.senior.percentageScreened}
                                        variant="info"
                                        style={{ height: '8px' }}
                                        className="mb-2"
                                    />
                                    <div className="text-center">
                                        <Badge bg="info">{deptEdStudents.senior.percentageScreened}% Screened</Badge>
                                    </div>
                                </div>
                            </Col>

                            <Col md={4}>
                                <div className="p-3 rounded border border-primary" style={{ backgroundColor: '#3b82f610' }}>
                                    <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem' }}>
                                        Overall Total
                                    </h6>
                                    <div className="d-flex justify-content-between mb-2" style={{ fontSize: '0.875rem' }}>
                                        <span className="text-muted">Total Students:</span>
                                        <strong>{deptEdStudents.overall.totalStudents.toLocaleString()}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3" style={{ fontSize: '0.875rem' }}>
                                        <span className="text-muted">Screened:</span>
                                        <strong>{deptEdStudents.overall.screened.toLocaleString()}</strong>
                                    </div>
                                    <ProgressBar
                                        now={deptEdStudents.overall.percentageScreened}
                                        variant="primary"
                                        style={{ height: '8px' }}
                                        className="mb-2"
                                    />
                                    <div className="text-center">
                                        <Badge bg="primary">{deptEdStudents.overall.percentageScreened}% Screened</Badge>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>
        );
    };

    const toggleSection = (section) => {
        const willExpand = !expandedSections[section];

        setExpandedSections(prev => ({
            ...prev,
            [section]: willExpand
        }));

        if (willExpand) {
            loadSchoolList(section);
        }
    };

    const SchoolListCard = ({ title, schools, category, icon: Icon, iconColor, description, count }) => {
        const isExpanded = expandedSections[category];
        const isLoading = loadingSchools[category];
        const schoolCount = schools.length;

        return (
            <Card className="class-admin-card h-100">
                <Card.Header className="bg-white border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            {Icon && (
                                <div
                                    className="class-admin-icon me-2"
                                    style={{ backgroundColor: `${iconColor}15`, color: iconColor, padding: '8px' }}
                                >
                                    <Icon size={18} />
                                </div>
                            )}
                            <div>
                                <h6 className="mb-0 fw-bold">{title}</h6>
                                <small className="text-muted">{description}</small>
                            </div>
                        </div>
                        {count > 0 ? (
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => toggleSection(category)}
                                className="text-decoration-none"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        Loading...
                                    </>
                                ) : isExpanded ? (
                                    'Hide'
                                ) : (
                                    `View ${count} ${count === 1 ? 'school' : 'schools'}`
                                )}
                            </Button>
                        ) : (
                            <Badge bg="secondary">No schools</Badge>
                        )}
                    </div>
                </Card.Header>
                {isExpanded && !isLoading && (
                    <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {schools.length === 0 ? (
                            <div className="text-center text-muted py-4">
                                <p className="mb-1">No schools currently in this category.</p>
                                <p className="small mb-0">
                                    {category === 'hot' && "Great news! No schools need immediate attention."}
                                    {category === 'warm' && "Schools in this range may move to Hot with some support."}
                                    {category === 'cool' && "These schools need strategic intervention to improve engagement."}
                                    {category === 'cold' && "Consider focusing resources on higher-priority trials."}
                                </p>
                            </div>
                        ) : (
                            <div className="list-group list-group-flush">
                                {schools.map((school, idx) => (
                                    <div
                                        key={school.schoolId}
                                        className="list-group-item px-0 py-3"
                                        style={{ borderBottom: idx === schools.length - 1 ? 'none' : '1px solid #e5e7eb' }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h6 className="mb-1 fw-bold">{school.schoolName}</h6>
                                                <div className="d-flex gap-2 mb-2">
                                                    <small className="text-muted">
                                                        <strong>Roll:</strong> {school.rollNumber}
                                                    </small>
                                                    {school.area && (
                                                        <small className="text-muted">
                                                            <strong>•</strong> {school.area}
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="d-flex gap-3 small">
                                                    <span className="text-muted">
                                                        <strong>Junior:</strong> {school.juniorScreened}/{school.doeJuniorInfants} ({school.juniorCompletionRate}%)
                                                    </span>
                                                    <span className="text-muted">
                                                        <strong>Senior:</strong> {school.seniorScreened}/{school.doeSeniorInfants} ({school.seniorCompletionRate}%)
                                                    </span>
                                                </div>
                                            </div>
                                            <Badge
                                                bg={school.overallCompletionRate >= 75 ? 'success' :
                                                    school.overallCompletionRate >= 50 ? 'primary' :
                                                        school.overallCompletionRate >= 25 ? 'warning' : 'danger'}
                                                className="ms-2"
                                            >
                                                {school.overallCompletionRate}% Overall
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                )}
            </Card>
        );
    };

    if (loadingConfig) {
        return (
            <div>
                <Container fluid className="class-admin-container">
                    <HeadingBanner
                        name={`Welcome ${name}!`}
                        isLoggedIn={true}
                        cloud={true}
                        teacherid={teacherid}
                    />
                    <Row className="class-admin-row">
                        <LeftPanel />
                        <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">
                            <div className="text-center py-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                                <p className="mt-3">Loading configuration...</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div>
            <Container fluid className="class-admin-container">
                <HeadingBanner
                    name={`Welcome ${name}!`}
                    isLoggedIn={true}
                    cloud={true}
                    teacherid={teacherid}
                />

                <Row className="class-admin-row">
                    <LeftPanel />
                    <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

                        <div className="class-admin-header">
                            <h2 className="class-admin-page-title">Trial Tracker Dashboard</h2>
                            <p className="class-admin-page-subtitle">Monitor trial health and prioritize customer outreach</p>
                        </div>

                        {/* Configuration Panel */}
                        <Card className="class-admin-card mb-4">
                            <Card.Header className="bg-white">
                                <h6 className="mb-0 fw-bold">Dashboard Configuration</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">
                                                Country <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={config.countryId}
                                                onChange={(e) => handleConfigChange('countryId', e.target.value)}
                                                disabled={true}
                                            >
                                                <option value="">Select Country</option>
                                                {countries.map(country => (
                                                    <option key={country.id} value={country.id}>
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Fixed to Republic of Ireland
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Academic Year</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={config.academicYear}
                                                onChange={(e) => handleConfigChange('academicYear', e.target.value)}
                                                placeholder="25-26"
                                            />
                                            <Form.Text className="text-muted">
                                                School year for filtering classes/students
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">
                                                Active Trials Since
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Only includes trials that started on or after this date AND are still active (not expired).
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span className="ms-1" style={{ cursor: 'help' }}>
                                                        <FaInfoCircle size={12} />
                                                    </span>
                                                </OverlayTrigger>
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={config.trialStartDate}
                                                onChange={(e) => handleConfigChange('trialStartDate', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                Filters to currently active trials that started on/after this date
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Screening Start Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={config.screeningStartDate}
                                                onChange={(e) => handleConfigChange('screeningStartDate', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                Count screenings completed on/after this date
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">DOE Enrollment Year</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={config.doeEnrollmentYear}
                                                onChange={(e) => handleConfigChange('doeEnrollmentYear', e.target.value)}
                                                placeholder="2024"
                                            />
                                            <Form.Text className="text-muted">
                                                Year of government enrollment data to use
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">
                                                Grade 1 Screener <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={config.grade1AssessmentId}
                                                onChange={(e) => handleConfigChange('grade1AssessmentId', e.target.value)}
                                                disabled={!config.countryId || grade1Screeners.length === 0}
                                            >
                                                <option value="">Select Grade 1 Screener</option>
                                                {grade1Screeners.map(screener => (
                                                    <option key={screener.id} value={screener.id}>
                                                        {screener.name || screener.title || `Assessment ${screener.id}`}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {grade1Screeners.length === 0 && config.countryId && (
                                                <Form.Text className="text-warning">
                                                    No Grade 1 screeners found for selected country
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">
                                                Grade 2 Screener <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={config.grade2AssessmentId}
                                                onChange={(e) => handleConfigChange('grade2AssessmentId', e.target.value)}
                                                disabled={!config.countryId || grade2Screeners.length === 0}
                                            >
                                                <option value="">Select Grade 2 Screener</option>
                                                {grade2Screeners.map(screener => (
                                                    <option key={screener.id} value={screener.id}>
                                                        {screener.name || screener.title || `Assessment ${screener.id}`}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {grade2Screeners.length === 0 && config.countryId && (
                                                <Form.Text className="text-warning">
                                                    No Grade 2 screeners found for selected country
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12}>
                                        <Button
                                            variant="primary"
                                            onClick={loadDashboardData}
                                            disabled={loading || !config.countryId || !config.grade1AssessmentId || !config.grade2AssessmentId}
                                            className="w-100"
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Loading Data...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSync className="me-2" />
                                                    Load Dashboard Data
                                                </>
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {dataLoaded && conversionData && (
                            <>
                                {/* Country Info Badge */}
                                <Alert variant="info" className="mb-4">
                                    <strong>Viewing data for:</strong> {getCountryName(config.countryId)} •
                                    <strong> Academic Year:</strong> {config.academicYear} •
                                    <strong> Active Trials Since:</strong> {config.trialStartDate}
                                </Alert>

                                {/* Conversion Overview */}
                                <div className="mb-4">
                                    <SectionHeader 
                                        title="Conversion Overview (Junior Infants Only)"
                                        tooltip="Summary of trial performance and expected customer conversions based on screening completion rates"
                                    />
                                    <Row>
                                        <Col md={4} className="mb-3">
                                            <MetricCard
                                                title="Total Schools in Trial"
                                                value={conversionData.total_schools_in_trial}
                                                subtitle={`${conversionData.total_juniors_in_trial.toLocaleString()} junior infants enrolled`}
                                                icon={FaSchool}
                                                color="#3b82f6"
                                            />
                                        </Col>
                                        <Col md={4} className="mb-3">
                                            <MetricCard
                                                title="Overall Completion Rate"
                                                value={`${conversionData.overall_completion_rate_pct}%`}
                                                subtitle={`${conversionData.total_completed.toLocaleString()} students completed screenings`}
                                                icon={FaChartLine}
                                                color="#10b981"
                                            />
                                        </Col>
                                        <Col md={4} className="mb-3">
                                            <MetricCard
                                                title="Expected Converting Students"
                                                value={conversionData.expected_junior_customers}
                                                subtitle={`${conversionData.probability_juniors_become_customers_pct}% weighted probability`}
                                                icon={FaFire}
                                                color="#f59e0b"
                                                tooltip="Weighted prediction: 70%+ completion = 100% probability, 50-69% = 60%, 30-49% = 25%, below 30% = 10%. This is your key metric for forecasting paying customers."
                                            />
                                        </Col>
                                    </Row>
                                </div>

                                {/* Department of Education Metrics */}
                                <DeptEdMetricsCard />

                                {/* Trial Health by Screening Rate */}
                                <div className="mb-4 mt-4">
                                    <SectionHeader 
                                        title="Trial Health by Screening Rate"
                                        tooltip="Distribution of schools across completion rate categories. Focus on Hot (75-100%) and Warm (50-74%) trials for best conversion outcomes."
                                    />
                                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                                        Schools with 75%+ completion are highly likely to convert to paying customers
                                    </p>
                                    <Row>
                                        {trialHealthData.map((item, idx) => (
                                            <Col key={idx} md={3} className="mb-3 mb-md-0">
                                                <HealthCard data={item} />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>

                                {/* Priority School Lists */}
                                <div className="mb-4">
                                    <SectionHeader 
                                        title="Priority School Lists"
                                        tooltip="Actionable school lists prioritized by conversion likelihood. Contact Hot trials first, then support Warm trials to reach 75%+ completion."
                                    />
                                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                                        These schools are highly likely to convert - prioritize outreach now
                                    </p>
                                    <Row>
                                        <Col md={6} className="mb-3">
                                            <SchoolListCard
                                                title="🔥 Hot Trials (75-100%)"
                                                schools={hotTrialSchools}
                                                category="hot"
                                                icon={FaFire}
                                                iconColor="#ef4444"
                                                description="Almost certain to convert"
                                                count={hotTrialsData?.trial_schools_total || 0}
                                            />
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <SchoolListCard
                                                title="⚠️ Warm Trials (50-74%)"
                                                schools={warmTrialSchools}
                                                category="warm"
                                                icon={FaExclamationTriangle}
                                                iconColor="#f59e0b"
                                                description="Need some support to reach 75%+"
                                                count={warmTrialsData?.trial_schools_total || 0}
                                            />
                                        </Col>
                                    </Row>
                                </div>

                                {/* Lower Priority Trials */}
                                <div className="mb-4">
                                    <SectionHeader 
                                        title="Lower Priority Trials"
                                        tooltip="Schools requiring significant intervention. Consider resource allocation carefully - focus may be better spent on Hot and Warm trials."
                                    />
                                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                                        Schools that need significant intervention or may require deprioritization
                                    </p>
                                    <Row>
                                        <Col md={6} className="mb-3">
                                            <SchoolListCard
                                                title="❄️ Cool Trials (25-49%)"
                                                schools={coolTrialSchools}
                                                category="cool"
                                                icon={FaThermometerHalf}
                                                iconColor="#06b6d4"
                                                description="Need substantial strategic intervention"
                                                count={coolTrialsData?.trial_schools_total || 0}
                                            />
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <SchoolListCard
                                                title="🧊 Cold Trials (0-24%)"
                                                schools={coldTrialSchools}
                                                category="cold"
                                                icon={FaSnowflake}
                                                iconColor="#8b5cf6"
                                                description="Consider deprioritizing for resource efficiency"
                                                count={coldTrialsData?.trial_schools_total || 0}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </>
                        )}

                        {!dataLoaded && !loading && (
                            <Alert variant="info">
                                <Alert.Heading>Welcome to Trial Tracker Dashboard</Alert.Heading>
                                <p>Configure the parameters above and click "Load Dashboard Data" to view trial metrics for active trials.</p>
                                <hr />
                                <p className="mb-0">
                                    <strong>Key Features:</strong>
                                </p>
                                <ul className="mb-0 mt-2">
                                    <li>Track screening completion rates across active trials</li>
                                    <li>Identify high-priority schools for conversion outreach</li>
                                    <li>Monitor progress against Department of Education enrollment data</li>
                                    <li>Focus resources on trials most likely to convert</li>
                                </ul>
                            </Alert>
                        )}

                    </Col>
                </Row>
            </Container>

            {isPortrait && <OrientationPrompt />}
        </div>
    );
}

export default TrialTrackerDash;